import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material'
import { withStyles } from '@mui/styles'
import React, { Fragment, useState } from 'react'
import HelpIcon from '@mui/icons-material/Help'
import axios from 'axios'
import { ApiConfig } from '../../config/api'
import { ApiResponse } from '../../models/ApiResponse'
import { SelectedFiltersValues } from './types/SelectedFilterValues'
import { ControlsSelector } from './ControlsSelector'
import positionBusiness from '../Position/actions/positionBusiness'

interface ControlsUploadProps {
  editMode: boolean
}

export const ControlsUpload: React.FC<ControlsUploadProps> = ({ editMode }) => {
  const { handleClosePositionMassive } = positionBusiness()

  const initialStateFilters: SelectedFiltersValues = {
    action: editMode ? 'update' : 'create',
    warehouseId: 0,
    zoneId: 0,
    typepositionId: 0,
    activeId: -1
  }

  const [filters, setFilters] =
    useState<SelectedFiltersValues>(initialStateFilters)
  const maxFileSize = 2097152
  const [requireZone, setRequireZone] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [currentFile, setCurrentFile] = useState<any | undefined>(undefined)
  const [progress, setProgress] = useState<number>(0)
  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(
    undefined
  )

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const handleUpload = () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(undefined)
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    callUpload(selectedFile, (event) => {
      setProgress(Math.round((100 * event.loaded) / event.total))
    })
      .then((response: any) => {
        const apiResponse = response.data as ApiResponse
        setIsUploading(false)

        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        setProgress(0)

        if (apiResponse.success) {
          handleClosePositionMassive(
            true,
            'El archivo ha sido cargado para su procesamiento'
          )
        } else {
          handleClosePositionMassive(
            true,
            `Error en carga de archivo: ${
              apiResponse.message || 'falla en el servicio.'
            }`
          )
        }
      })
      .catch((error: any) => {
        setIsUploading(false)
        setProgress(0)
        console.log(error)
        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        setProgress(0)
        handleClosePositionMassive(
          true,
          `Error en carga de archivo: ${
            error.response?.data?.message || 'falla en el servicio.'
          }`
        )
      })
  }

  const validateButtonLoad = () => {
    // si ya se presiono el botón y está arriba bloquea el boton cargar nuevamente.
    const resultValidateControls = validateParamsToSend()
    if (!selectedFiles || isUploading || resultValidateControls) {
      return true
    }

    return false
  }

  const callUpload = (file: any, onUploadProgress: (event: any) => void) => {
    const formData = new FormData()
    formData.append('file', file)

    // agrega parametros
    Object.keys(filters).forEach((key) => {
      formData.append(key, filters[key])
    })

    const axiosInstance = axios.create({
      baseURL: ApiConfig.ProcessApi,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return axiosInstance.post(`/PositionMassiveUploadFile`, formData, {
      onUploadProgress
    })
  }

  const sizeLabel = (size: number): string => {
    let label = ''
    if (size / 1024 / 1024 > 1) label = (size / 1024 / 1024).toFixed(2) + ' MB'
    else if (size / 1024 > 1) label = (size / 1024).toFixed(0) + ' KB'
    else label = size.toFixed(2) + ' Bytes'
    return label
  }

  const validateFile = (files: any[] | undefined): string => {
    if (!files || files.length === 0) {
      return 'Ningún archivo seleccionado'
    }

    const selectedFile = files[0]

    if (selectedFile.size > maxFileSize) {
      const maxLabel = sizeLabel(maxFileSize)
      return `El archivo seleccionado supera el tamaño máximo: ${maxLabel}`
    }

    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) {
      return `El archivo seleccionado no es de tipo Excel (.xls)`
    }

    return ''
  }

  const BorderLinearProgress = withStyles(() => ({
    root: {
      height: 15,
      borderRadius: 5
    },
    colorPrimary: {
      backgroundColor: '#EEEEEE'
    },
    bar: {
      borderRadius: 5,
      backgroundColor: '#1a90ff'
    }
  }))(LinearProgress)

  // controles selectores
  const handleChangeDDLOption = (e: any) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const validateParamsToSend = () => {
    // Solo son obligatorios al crear
    if (!editMode) {
      if (filters.warehouseId === undefined || filters.warehouseId <= 0) {
        return true
      }

      if (filters.zoneId === undefined || filters.zoneId <= 0) {
        return true
      }

      if (filters.typepositionId === undefined || filters.typepositionId <= 0) {
        return true
      }

      if (filters.activeId === undefined || filters.activeId < 0) {
        return true
      }
    }

    if (requireZone) {
      if (filters.zoneId !== undefined && filters.zoneId <= 0) {
        return true
      } else {
        return false
      }
    }

    return false
  }

  return (
    <Fragment>
      <ControlsSelector
        handleChangeDDLOption={handleChangeDDLOption}
        filtersRequest={filters}
        editMode={editMode}
        isUploading={!!selectedFiles}
        setRequireZone={setRequireZone}
      />
      <Box display='flex' marginTop={3} alignItems='center'>
        <Grid
          container
          spacing={3}
          direction='row'
          alignItems='center'
          justifyContent='center'
          style={{ justifyContent: 'center' }}
        >
          <Grid item xs={3} style={{ textAlign: 'right' }}>
            <label htmlFor='btn-upload'>
              <input
                id='btn-upload'
                name='btn-upload'
                style={{ display: 'none' }}
                type='file'
                onChange={handleSelectFile}
                accept='.xls,.xlsx'
              />
              <Tooltip
                title='Seleccionar el archivo Excel a cargar'
                placement='top-end'
              >
                <Button
                  style={{
                    maxHeight: '40px',
                    width: '200px',
                    overflow: 'hidden'
                  }}
                  // className='btn-choose'
                  variant='outlined'
                  component='span'
                >
                  Seleccionar Archivo
                </Button>
              </Tooltip>
            </label>
          </Grid>

          <Grid item xs={5}>
            <div className='file-name'>
              {selectedFiles && selectedFiles.length > 0
                ? selectedFiles[0].name
                : null}
            </div>
          </Grid>
          <Grid item xs={1}>
            <Tooltip
              title='Enviar el archivo seleccionado al servidor para su procesamiento'
              placement='top-end'
            >
              <Button
                className='btn-upload'
                color='primary'
                variant='contained'
                component='span'
                disabled={validateButtonLoad()}
                onClick={handleUpload}
              >
                Cargar
              </Button>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs={1}
            alignContent='center'
            style={{ textIndent: '15px' }}
          >
            <Tooltip
              title='Oprima el botón SELECCIONAR para abrir el archivo a cargar y luego el botón CARGAR para enviar el archivo al servidor para su procesamiento'
              placement='top-end'
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <div
        style={{
          margin: '10px',
          visibility: currentFile ? 'visible' : 'hidden'
        }}
      >
        <BorderLinearProgress variant='determinate' value={progress} />

        <Box minWidth={35}>
          <Typography
            variant='body1'
            color='textSecondary'
            style={{ textAlign: 'end' }}
          >{`${progress}%`}</Typography>
        </Box>
      </div>
    </Fragment>
  )
}
